/**
 * Default fantasy rules. This file is runtime-agnostic (no Deno/Node/RN APIs,
 * no imports) — it is consumed by the Deno scoring Edge Function, the Expo
 * client (via the `@scoring/*` alias) and the Node test runner.
 *
 * The values here are DEFAULTS: each fantasy season stores its own copy in
 * `fantasy_seasons.formation_rules`, so tuning a rule is a data change.
 */

export type Position = 'GK' | 'DEF' | 'MID' | 'FWD';

export interface ScoringRules {
  /** Appearance points: 1–59 minutes / 60+ minutes. */
  minutes: { played: number; played60: number };
  goal: Record<Position, number>;
  assist: number;
  /** Requires 60+ minutes and zero goals conceded while any minutes played. */
  cleanSheet: Record<Position, number>;
  /** GK earns 1 point per this many saves. */
  savesPerPoint: number;
  penaltySaved: number;
  penaltyMissed: number;
  /** GK/DEF lose `goalsConcededPoints` per `goalsConcededPer` conceded. */
  goalsConcededPer: number;
  goalsConcededPoints: number;
  yellow: number;
  red: number;
  ownGoal: number;
  captainMultiplier: number;
  /** Bonus for the top performers of each fixture, best first. */
  bonus: number[];
}

export interface SquadRules {
  /** Total squad quota per position. */
  squad: Record<Position, number>;
  /** Starting XI size and [min, max] per position. */
  starting: { size: number } & Record<Position, [number, number]>;
}

export interface FormationRules extends SquadRules {
  scoring: ScoringRules;
}

export const DEFAULT_SCORING_RULES: ScoringRules = {
  minutes: { played: 1, played60: 2 },
  goal: { GK: 6, DEF: 6, MID: 5, FWD: 4 },
  assist: 3,
  cleanSheet: { GK: 4, DEF: 4, MID: 1, FWD: 0 },
  savesPerPoint: 3,
  penaltySaved: 5,
  penaltyMissed: -2,
  goalsConcededPer: 2,
  goalsConcededPoints: -1,
  yellow: -1,
  red: -3,
  ownGoal: -2,
  captainMultiplier: 2,
  bonus: [3, 2, 1],
};

export const DEFAULT_FORMATION_RULES: FormationRules = {
  squad: { GK: 2, DEF: 5, MID: 5, FWD: 3 },
  starting: { size: 11, GK: [1, 1], DEF: [3, 5], MID: [2, 5], FWD: [1, 3] },
  scoring: DEFAULT_SCORING_RULES,
};

/**
 * Weights of the per-fixture performance index that decides bonus points.
 * Defensive contributions count, so non-scorers can earn bonus too.
 */
export const BONUS_INDEX_WEIGHTS = {
  goals: 8,
  assists: 5,
  saves: 1,
  tackles: 0.6,
  passes: 0.02,
  rating: 2,
  goalsConceded: -1,
  yellow: -1,
  red: -3,
  ownGoals: -4,
  penaltyMissed: -2,
  penaltySaved: 5,
} as const;

/**
 * World Cup nation-progression bonus: points each featuring squad player earns
 * when their nation advanced into the round being scored. One source of truth
 * for the scoring engine and the demo seed.
 */
export const WC_NATION_PROGRESSION_BONUS = 3;

/**
 * Spin Boost tuning (the 24h bonus mini-game). A drawn spin player earns
 * SPIN_BONUS_PERCENTAGE of their gameweek points, but only on a GOOD game —
 * at least SPIN_GOOD_GAME_POINTS. Tuned so a strong game (a goal + bonus ≈ 9)
 * returns a meaningful, bounded reward and a quiet game returns nothing.
 */
export const SPIN_GOOD_GAME_POINTS = 6;
export const SPIN_BONUS_PERCENTAGE = 50;
